import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useAssertedParams } from '../../../lib/utils';
import {
  FinalizeNewBusinessModuleRule,
  Customer,
  PaymentMethodDtoWithIsPreferred,
  RulePayer,
  legacyPermissionsControllerAtMeQueryOptions,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  keyValueFieldsToObject,
  policyControllerListPolicyDocumentsQueryOptions,
  derogationControllerCheckDerogationRequirementQueryOptions,
} from 'korint-sdk';
import { CheckSquareIcon, SquareIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import dompurify from 'dompurify';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';

type FinalizeNewBusinessToDoListProps = {
  rules: FinalizeNewBusinessModuleRule[];
  customer: Customer;
  customers: Customer[];
  payers: RulePayer[] | undefined;
  paymentMethods: PaymentMethodDtoWithIsPreferred[];
  hasUnacknowledgedAlerts: boolean;
};

export function FinalizeNewBusinessToDoList({
  rules,
  customer,
  customers,
  payers,
  paymentMethods,
  hasUnacknowledgedAlerts,
}: FinalizeNewBusinessToDoListProps) {
  const { policy, workingBranch } = usePolicyOnDashboard();
  const { branchId } = useAssertedParams();
  const { t } = useTranslation();

  const { data: brokerageFirmId } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    select: data => data.brokerageFirmId,
  });

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    select: data => ({ ...data, customFields: keyValueFieldsToObject(data.customFields ?? []) }),
    enabled: Boolean(brokerageFirmId),
  });

  const { data: documents } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id!, { branchId }),
    select: data => data.data,
    enabled: Boolean(policy.id),
  });

  /**
   * @debt see what we do for SIGN action derogation
   */

  const { data: needsDerogation } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      branchId,
      {},
      { action: 'CONFIRM' },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(branchId),
  });

  return (
    <>
      <div
        className="text-sm"
        dangerouslySetInnerHTML={{
          __html: dompurify.sanitize(
            t([
              `${policy.productId}.finalizeNewBusiness.description`,
              'finalizeNewBusiness.description',
            ]),
          ),
        }}
      />
      <Stack asChild>
        <ul>
          {rules?.map((item, index) => {
            // Unresolved payers stay unchecked instead of judging a partial list
            const isValid =
              payers !== undefined &&
              item.isValid({
                customer,
                policy,
                customers,
                payers,
                documents: documents ?? [],
                paymentMethods: paymentMethods ?? [],
                hasUnacknowledgedAlerts: Boolean(hasUnacknowledgedAlerts),
                brokerageFirm,
                branch: workingBranch,
                needsDerogation: Boolean(needsDerogation),
              });

            const link =
              typeof item.link === 'function'
                ? item.link({ policyId: policy.id, branchId, customerId: customer.id })
                : item.link;

            // A rule can carry a labelOffline variant: shown when every payer is OFFLINE, so a
            // "payment method required" item never reads as satisfied by a method that is not there.
            const allPayersOffline =
              payers !== undefined &&
              payers.length > 0 &&
              payers.every(({ customer: payer }) => payer.paymentMode === 'OFFLINE');
            const label = item.id
              ? t(
                  allPayersOffline
                    ? [
                        `finalizeNewBusiness.rules.${item.id}.labelOffline`,
                        `finalizeNewBusiness.rules.${item.id}.label`,
                      ]
                    : `finalizeNewBusiness.rules.${item.id}.label`,
                )
              : '';

            return (
              <li key={index} className="flex items-center space-x-2 text-sm">
                {isValid ? <CheckSquareIcon /> : <SquareIcon />}
                {link ? (
                  <Link to={link} className="underline">
                    {label}
                  </Link>
                ) : (
                  <span>{label}</span>
                )}
              </li>
            );
          })}
        </ul>
      </Stack>
    </>
  );
}
